/**
 * Data hooks for §5 Contacts.
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as contactsApi from '../../api/contacts.api.js';
import * as accountsApi from '../../api/accounts.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';

/** @param {object} params e.g. `{ q, account_id, buying_role, influence, preferred_channel }` */
export function useContacts(params) {
  return useQuery({
    queryKey: queryKeys.contacts.list(params),
    queryFn: () => contactsApi.listContacts(params),
    placeholderData: (previous) => previous,
  });
}

/** One contact. @param {string} id @param {{include?:string[]}} [options] */
export function useContact(id, options = {}) {
  return useQuery({
    queryKey: queryKeys.contacts.detail(id),
    queryFn: () => contactsApi.getContact(id, options),
    enabled: Boolean(id),
  });
}

/** Birthdays and work/customer anniversaries due within `withinDays`. */
export function useContactAnniversaries(withinDays, enabled = true) {
  return useQuery({
    queryKey: queryKeys.contacts.occasions(withinDays),
    queryFn: () => contactsApi.listContactAnniversaries(withinDays),
    enabled,
  });
}

/**
 * Live duplicate check. `id` is `'new'` for the create form; `candidate`
 * carries whatever the user has typed so far (email/phone/last_name/account_id).
 * Disabled until there is at least one signal worth matching on.
 */
export function useContactDuplicates(id, candidate, enabled) {
  return useQuery({
    queryKey: queryKeys.contacts.duplicates(id, candidate),
    queryFn: () => contactsApi.checkContactDuplicates(id, candidate),
    enabled: Boolean(enabled),
    staleTime: 10_000,
  });
}

export function useContactRelationships(id, enabled = true) {
  return useQuery({
    queryKey: queryKeys.contacts.relationships(id),
    queryFn: () => contactsApi.listContactRelationships(id),
    enabled: Boolean(id) && enabled,
  });
}

export function useCreateContact() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload) => contactsApi.createContact(payload),
    onSuccess: (contact) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });
      dispatch(pushToast({ tone: 'success', message: `Contact "${contact.full_name}" created.` }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not create that contact', detail: error.message }));
    },
  });
}

export function useUpdateContact() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, patch, rowVersion }) => contactsApi.updateContact(id, patch, rowVersion),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all }),
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : 'Could not save that change',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}

export function useDeleteContact() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, reason, rowVersion }) => contactsApi.deleteContact(id, { reason, rowVersion }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });
      dispatch(pushToast({ tone: 'success', message: 'Contact deleted.' }));
    },
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : 'Could not delete that contact',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}

export function useRestoreContact() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (id) => contactsApi.restoreContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.all });
      dispatch(pushToast({ tone: 'success', message: 'Contact restored.' }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not restore that contact', detail: error.message }));
    },
  });
}

export function useAddContactRelationship() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, payload }) => contactsApi.addContactRelationship(id, payload),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.relationships(id) });
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not add that relationship', detail: error.message }));
    },
  });
}

export function useRemoveContactRelationship() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, relationshipId }) => contactsApi.removeContactRelationship(id, relationshipId),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.contacts.relationships(id) });
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not remove that relationship', detail: error.message }));
    },
  });
}

/** Dropdown options the create/edit form needs: accounts (for the employer picker) and owners. */
export function useContactOptions() {
  const accounts = useQuery({
    queryKey: queryKeys.accounts.list({ limit: 200, sort: 'name' }),
    queryFn: () => accountsApi.listAccounts({ limit: 200, sort: 'name' }),
    staleTime: 300_000,
  });

  const accountRows = accounts.data?.data ?? [];

  return useMemo(() => ({
    accounts: accountRows.map((a) => ({ value: a.id, label: a.name })),
    loading: accounts.isLoading,
  }), [accountRows, accounts.isLoading]);
}
